import { Box, Container, Unstable_Grid2 as Grid, Stack } from "@mui/material";
import { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { DATE_TIME_FORMAT } from "../../app/constants/common";
import { PRIVATE_ROUTES } from "../../app/constants/routes";
import { uiToServer } from "../../app/utils/utilityFunctions";
import { SelectOptionsFieldWithCustomConfig } from "../../components/ui/InputUI";
import { useGetAttendanceStatsMutation, useGetDashboardConfigMutation } from "../../hooks/ApiHooks";
import { dashboardDateRangeSelectConfig } from "./config/customerTrendsConfig";
import { attendanceStatsDataConfig, breakdownStatsData, taskTypeCount } from "./config/dataConfig";
import CustomerMonthlyCountChart from "./CustomerMonthlyCountChart";
import { selectDashboardSelectedDateRange } from "./slice/dashboardSlice";
import StatCard from "./StatCard";
import TasksDonutChart from "./TasksDonutChart";

export default function Dashboard() {
  const { triggerMutation: getDashboardData, isLoading: isDashboardDataLoading, data } = useGetDashboardConfigMutation();
  const { triggerMutation: getAttendanceStats, isLoading: isAttendanceStatsLoading, data: attendanceStatsData } = useGetAttendanceStatsMutation();
  const { value: dateRangeValue, from, to } = useSelector(selectDashboardSelectedDateRange);

  const refetchData = useCallback(() => {
    getDashboardData(from, to);
    getAttendanceStats(uiToServer(new Date(), DATE_TIME_FORMAT.SERVER_DATE));
  }, [getDashboardData, getAttendanceStats, from, to]);

  useEffect(() => {
    refetchData();
  }, [refetchData]);

  const buildTaskListHref = useCallback(
    ({ type, status, customerChain } = {}) => {
      if (!type && !status && !customerChain) return undefined;
      const params = new URLSearchParams();
      if (type) params.set("type", type);
      if (status) params.set("status", Array.isArray(status) ? status.join(",") : status);
      if (customerChain) params.set("customerChain", customerChain);
      if (dateRangeValue !== undefined && dateRangeValue !== null) params.set("dateRange", String(dateRangeValue));
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      return `${PRIVATE_ROUTES.tasks}?${params.toString()}`;
    },
    [dateRangeValue, from, to]
  );

  return (
    <Box sx={{ py: 8, flex: 1, overflowY: "auto" }}>
      <Container maxWidth="xl">
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }}>
          <Box sx={{ width: { xs: "100%", sm: 220 } }}>
            <SelectOptionsFieldWithCustomConfig selectConfig={dashboardDateRangeSelectConfig} />
          </Box>
        </Stack>
        <Grid container spacing={3}>
          {[...attendanceStatsDataConfig(attendanceStatsData?.data), ...taskTypeCount(data?.data.typeCount)].map((item) => (
            <Grid xs={12} sm={6} lg={4} key={item.id}>
              <StatCard
                icon={item.icon}
                label={item.label}
                count={item.count}
                color={item.color}
                loading={isAttendanceStatsLoading || isDashboardDataLoading}
                href={buildTaskListHref({ type: item.type })}
              />
            </Grid>
          ))}
          <Grid xs={12} lg={8}>
            <TasksDonutChart refetchData={refetchData} data={data} loading={isDashboardDataLoading} />
          </Grid>
          <Grid xs={12} lg={4}>
            <Stack height="100%" gap={2}>
              {breakdownStatsData(data?.data.breakdown).map((item) => (
                <StatCard
                  key={item.id}
                  color={item.color}
                  icon={item.icon}
                  count={item.count}
                  label={item.label}
                  loading={isDashboardDataLoading}
                  iconSize={item.iconSize}
                  href={buildTaskListHref({
                    type: item.filterType,
                    status: item.filterStatus,
                    customerChain: item.filterCustomerChain,
                  })}
                />
              ))}
            </Stack>
          </Grid>
          <Grid xs={12}>
            <CustomerMonthlyCountChart from={from} to={to} />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
